import React, { useMemo } from 'react';

import TagsFilterButton from './TagsFilterButton';

type Tag = {
  id: number;
  name: string;
  color?: string;
};

type FilterItem = {
  field: string;
  conditions: { op: string; val: number[] }[];
  label?: string;
};

const BLANK_TAG = { id: 0, name: 'Blank' };

export const TagsFilter = ({
  tags,
  filter,
  setParams,
  buttonLabel = 'Tags',
  filterPopupWidth = '200px',
  searchPlaceholder = 'Search Tags',
}) => {
  const normalizedTags = useMemo<Tag[]>(() => {
    if (!tags || tags.length === 0) {
      return [];
    }
    const hasBlank = tags.some((t) => t.id === 0);
    return hasBlank ? tags : [BLANK_TAG, ...tags];
  }, [tags]);

  const allIds = useMemo<number[]>(
    () => Array.from(new Set(normalizedTags.map((t) => t.id))) as number[],
    [normalizedTags]
  );

  const checkedIds = useMemo<number[]>(() => {
    const tagFilter = Array.isArray(filter)
      ? (filter as FilterItem[]).find((f) => f.field === 'tags')
      : undefined;
    const condition = tagFilter?.conditions?.[0];
    return Array.isArray(condition?.val) ? (condition!.val as number[]) : [];
  }, [filter]);

  const upsertFieldFilter = (
    filters: FilterItem[] | undefined,
    field: string,
    vals: number[]
  ): FilterItem[] => {
    const next = Array.isArray(filters) ? [...filters] : [];
    if (!vals || vals.length === 0) {
      return next.filter((f) => f.field !== field);
    }
    const idx = next.findIndex((f) => f.field === field);
    const tagFilter: FilterItem = { field, conditions: [{ op: 'in', val: vals }] };
    if (idx === -1) next.push(tagFilter);
    else next[idx] = tagFilter;
    return next;
  };

  const setFilterParam = (nextFilters: FilterItem[]) => {
    if (!nextFilters || nextFilters.length === 0) {
      setParams({ filter: undefined });
    } else {
      setParams({ filter: nextFilters });
    }
  };

  const handleChangeCheckboxFilter = (tagId: number) => {
    const nextChecked = checkedIds.includes(tagId)
      ? checkedIds.filter((id) => id !== tagId)
      : [...checkedIds, tagId];

    const nextFilters = upsertFieldFilter(filter as FilterItem[] | undefined, 'tags', nextChecked);
    setFilterParam(nextFilters);
  };

  const handleClickAllChecked = () => {
    const areAllChecked = allIds.length === checkedIds.length;

    const nextFilters = upsertFieldFilter(
      filter as FilterItem[] | undefined,
      'tags',
      areAllChecked ? [] : allIds
    );
    setParams({ filter: nextFilters });
  };

  const filterOptions = normalizedTags.map((tag) => ({
    id: tag.id,
    value: tag.id,
    label: tag.name,
    color: tag.color ?? '',
    isChecked: checkedIds.includes(tag.id),
  }));

  return (
    <TagsFilterButton
      buttonLabel={buttonLabel}
      filterOptions={filterOptions}
      filterPopupWidth={filterPopupWidth}
      handleChangeCheckboxFilter={handleChangeCheckboxFilter}
      handleClickAllChecked={handleClickAllChecked}
      count={checkedIds.length}
      hasSearch
      searchPlaceholder={searchPlaceholder}
    />
  );
};
